"use client"

import * as React from "react"
import { createPortal } from "react-dom"

/**
 * A slot in a section's title row that the active tab can fill.
 *
 * A tabbed section (My Branch, Settings) draws its own title above the tabs, but the main action of a tab
 * ("Inviter quelqu'un" on Team) belongs beside that title, not inside the tab's body. The layout renders the
 * outlet; the tab renders {@link PageTitleActions}, which portals its children there. Without a provider (or
 * on a page that draws no outlet) the actions render nothing, so a tab never shows a stray button.
 */
type PageTitleActionsContextValue = {
  target: HTMLElement | null
  setTarget: (element: HTMLElement | null) => void
}

const PageTitleActionsContext = React.createContext<PageTitleActionsContextValue | null>(null)

export function PageTitleActionsProvider({ children }: { children: React.ReactNode }) {
  const [target, setTarget] = React.useState<HTMLElement | null>(null)
  const value = React.useMemo(() => ({ target, setTarget }), [target])
  return <PageTitleActionsContext.Provider value={value}>{children}</PageTitleActionsContext.Provider>
}

/** Where the tab's title actions land. Place it in the layout's title row. */
export function PageTitleActionsOutlet({ className }: { className?: string }) {
  const context = React.useContext(PageTitleActionsContext)
  const setTarget = context?.setTarget
  const ref = React.useCallback((element: HTMLDivElement | null) => setTarget?.(element), [setTarget])
  if (!context) return null
  return <div ref={ref} className={className ?? "flex items-center gap-2 empty:hidden"} />
}

/** The tab's actions, drawn in the layout's title row. */
export function PageTitleActions({ children }: { children: React.ReactNode }) {
  const context = React.useContext(PageTitleActionsContext)
  if (!context?.target) return null
  return createPortal(children, context.target)
}
